//* MUI imports
import { Box, SxProps } from '@mui/material';

import React from 'react';
import { pxToRem, pxTovW } from '../../../commonUtils/resizeUtils';
import { IStyles } from '../../../commonUtils/styleUtils';
import HeaderIcon, { HeaderIconProps } from '../HeaderIcon';

import { FeatureIconV2 } from '@geneo2-web/shared-ui';
//* Interface
interface HeaderIconWrapperProps {
  icons: HeaderIconProps[];
  featureIconStyles?: SxProps;
}

const styles: IStyles = {
  root: {
    boxSizing: 'border-box',
    width: '100%',
    display: { xs: 'inline-table', md: 'flex' },
    backgroundColor: '#fff',
    boxShadow: { xs: 'none', md: '-1px 3px 8px -2px rgba(0,0,0,0.30)' },
    justifyContent: 'center',

    p: {
      xs: `${pxToRem(17)} ${pxToRem(10)}`,
      md: pxTovW(14),
    },
    overflow: 'scroll',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  container: {
    width: { xs: '100%' },

    display: 'flex',

    justifyContent: {
      xs: 'space-between',
      md: 'center',
    },

    '&>div': {
      width: 'max-content',
    },
    border: 'none',
  },
};

const isLaboratoryIcon = (icon: HeaderIconProps) =>
  icon.fileName === 'laboratory' || icon.cardText === 'Laboratory';

//* Layout that fits the extra Laboratory card in one row - only applied when it is shown.
const laboratoryLayoutStyles = {
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: { xs: pxToRem(12), md: pxToRem(16), lg: pxTovW(24) },
    flexWrap: { xs: 'wrap', lg: 'nowrap' },
    padding: { xs: pxToRem(0), lg: pxTovW(20) },
  },
  iconRoot: {
    width: { xs: pxToRem(95), md: pxToRem(110), lg: pxTovW(341.5) },
    maxWidth: '100%',
    flex: { lg: `0 1 ${pxTovW(341.5)}` },
    padding: {
      xs: pxToRem(8),
      lg: `${pxTovW(15)} ${pxTovW(20)} ${pxTovW(15)} ${pxTovW(28)}`,
    },
    marginX: { xs: 'auto', lg: 0 },
  },
  iconImage: {
    height: { xs: pxToRem(40), lg: pxTovW(45) },
    flexShrink: 0,
  },
};

export const HeaderIconWrapperV2: React.FC<HeaderIconWrapperProps> = (
  props
) => {
  const { icons, featureIconStyles } = props;
  if (!icons.length) {
    return null;
  }
  const hasLaboratory = icons.some(
    (icon) => !icon.isHidden && isLaboratoryIcon(icon)
  );
  return (
    <Box sx={styles.root}>
      <Box
        sx={{
          ...styles,
          display: 'flex', // { xs: 'list-item', lg: 'flex' },

          gap: { xs: pxTovW(20), lg: pxTovW(50) },
          flexWrap: 'wrap',
          padding: { xs: pxTovW(0), lg: pxTovW(20) },
          ...(hasLaboratory ? laboratoryLayoutStyles.container : {}),
        }}
      >
        {icons.map((icon, index) => {
          if (icon.isHidden) return null;

          const laboratoryStyles = isLaboratoryIcon(icon)
            ? {
                backgroundColor: '#FFFFFF',
                border: '1px solid transparent',
                backgroundImage:
                  'linear-gradient(#FFFFFF, #FFFFFF), linear-gradient(90deg, #A5247F, #475D99)',
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
                '&:hover': {
                  backgroundColor: '#FFFFFF',
                  border: '2px solid transparent',
                  backgroundImage:
                    'linear-gradient(#FFFFFF, #FFFFFF), linear-gradient(90deg, #A5247F, #475D99)',
                  backgroundOrigin: 'border-box',
                  backgroundClip: 'padding-box, border-box',
                },
              }
            : undefined;

          const iconRootStyles = {
            ...(hasLaboratory ? laboratoryLayoutStyles.iconRoot : {}),
            ...featureIconStyles,
            ...(laboratoryStyles ?? {}),
          };

          return (
            <FeatureIconV2
              key={`HeaderIcon-${index}`}
              fileName={icon.fileName}
              imageType={icon.type || 'png'}
              cardText={icon.cardText ?? ''}
              parentFolder="icons"
              onClick={icon.onClick} // ✅ Fixed here
              isDisabled={icon.isDisabled}
              rootStyles={iconRootStyles}
              imageStyles={
                hasLaboratory ? laboratoryLayoutStyles.iconImage : undefined
              }
              dataId={icon.dataId}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default HeaderIconWrapperV2;
